import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import {
  CalibrateLaserWeedJobRequest,
  CalibrateLaserWeedObjectiveRequest,
  Checklist,
  ChecklistAutomationCode,
  checklistAutomationCodeToJSON,
  ChecklistDefinition,
  ChecklistExemption,
  ChecklistItemDefinition,
  CloneJobRequest,
  CloneJobResponse,
  CreateEnterRowTaskRequest,
  CreateExitRowTaskRequest,
  CreateFollowPathTaskRequest,
  CreateJobRequest,
  CreateJobsRequest,
  GroundPrepJobConfiguration,
  InSeasonJobConfiguration,
  Intervention,
  InterventionAssignment,
  Job,
  JobConfiguration,
  JobList,
  LambdaPathPlannerRequest,
  LaserWeedJobConfiguration,
  ListChecklistDefinitionsResponse,
  ListInterventionsRequest,
  ListInterventionsResponse,
  ListJobsResponse,
  ListObjectiveAssignmentsResponse,
  ListTasksResponse,
  Objective,
  ObjectiveAssignment,
  ObjectiveAssignmentList,
  ObjectiveList,
  PivotRiskObjectivesResponse,
  StartManualTaskRequest,
  State,
  stateToJSON,
  StopManualTaskRequest,
  Task,
  TransportJobConfiguration,
  UpdateTaskRequest,
  UpdateTaskResponse,
} from "protos/rtc/jobs";
import { camelToSnake } from "common/utils/strings";
import {
  ClockInWatcherResponse,
  CreatePoolResponse,
  GetNextRobotRequest,
  GetNextRobotResponse,
  GetPoolResponse,
  GetWatcherAssignmentResponse,
  ListActiveRobotsResponse,
  ListActiveWatchersResponse,
  ListPoolRobotsResponse,
  ListPoolWatchersResponse,
  ListRobotAssignmentsResponse,
  ListWatchPoolsResponse,
} from "protos/rtc/rocc";
import { Duration } from "protos/google/protobuf/duration";
import { JobType, jobTypeToJSON } from "protos/atk/job_type";
import { Method } from "common/utils/api";
import { QueryEnv } from "common/state/queryEnv";
import { RigActivitiesConfiguration } from "protos/atk/activities";
import { RowTraversalSpeed, TransportSpeed } from "protos/rtc/tractor";
import { ScvAutonomyActivitiesConfiguration } from "protos/atk/autotractor";
import { spliceIfExists } from "common/utils/arrays";
import { ToolSpeed, TransitionSpeed } from "protos/rtc/implement";
import { toQuery } from "common/utils/browser";
import { transformKeys } from "common/utils/objects";

interface LaserWeedJob extends Job {
  type: JobType.LASER_WEED;
}

interface GroundPrepJob extends Job {
  type: JobType.GROUND_PREP;
}

interface TransportJob extends Job {
  type: JobType.TRANSPORT;
}

interface InSeasonJob extends Job {
  type: JobType.IN_SEASON;
}

// this is necessary because we need a set of exhaustive, disjoint
// specializations of Job for AnyJob to be assignment-compatible with Job,
// so we can properly narrow the type of Job by switching on Job.type
interface OtherJob extends Job {
  type: Exclude<
    JobType,
    | JobType.LASER_WEED
    | JobType.GROUND_PREP
    | JobType.TRANSPORT
    | JobType.IN_SEASON
  >;
}

type AnyJob =
  | LaserWeedJob
  | GroundPrepJob
  | TransportJob
  | InSeasonJob
  | OtherJob;

interface ConfiguredLaserWeedJob {
  job: LaserWeedJob;
  laserWeed: LaserWeedJobConfiguration;
  groundPrep: undefined;
  transport: undefined;
  inSeason: undefined;
}

interface ConfiguredGroundPrepJob {
  job: GroundPrepJob;
  laserWeed: undefined;
  groundPrep: GroundPrepJobConfiguration;
  transport: undefined;
  inSeason: undefined;
}

interface ConfiguredTransportJob {
  job: TransportJob;
  laserWeed: undefined;
  groundPrep: undefined;
  transport: TransportJobConfiguration;
  inSeason: undefined;
}

interface ConfiguredInSeasonJob {
  job: InSeasonJob;
  laserWeed: undefined;
  groundPrep: undefined;
  transport: undefined;
  inSeason: InSeasonJobConfiguration;
}

export type ConfiguredJob =
  | ConfiguredLaserWeedJob
  | ConfiguredGroundPrepJob
  | ConfiguredTransportJob
  | ConfiguredInSeasonJob;

// Pairs a job with its type's configuration; undefined when the
// configuration doesn't match the job type.
export const configureJob = (
  job: AnyJob,
  jobConfig: JobConfiguration
): ConfiguredJob | undefined => {
  const none = {
    laserWeed: undefined,
    groundPrep: undefined,
    transport: undefined,
    inSeason: undefined,
  };
  switch (job.type) {
    case JobType.LASER_WEED: {
      return jobConfig.laserWeed
        ? { ...none, job, laserWeed: jobConfig.laserWeed }
        : undefined;
    }
    case JobType.GROUND_PREP: {
      return jobConfig.groundPrep
        ? { ...none, job, groundPrep: jobConfig.groundPrep }
        : undefined;
    }
    case JobType.TRANSPORT: {
      return jobConfig.transport
        ? { ...none, job, transport: jobConfig.transport }
        : undefined;
    }
    case JobType.IN_SEASON: {
      return jobConfig.inSeason
        ? { ...none, job, inSeason: jobConfig.inSeason }
        : undefined;
    }
    default: {
      return undefined;
    }
  }
};

const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (arguments_, api, extraOptions = {}) => {
  return await fetchBaseQuery({
    baseUrl: `${QueryEnv.get().rtcJobsBaseUrl}/v1/`,
    prepareHeaders: async (headers) => {
      const accessToken = await QueryEnv.get().accessTokenProvider(api);

      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }

      headers.set("X-AUTH0-audience", QueryEnv.get().auth0Audience);
      return headers;
    },
    paramsSerializer: (params: Record<string, any>) =>
      toQuery(params, api.endpoint),
  })(arguments_, api, extraOptions);
};

enum JobsAPITag {
  JOB = "Job",
  JOB_CONFIG = "JobConfig",
  /** @deprecated */
  SCV_ACTIVITIES = "ScvActivities",
  RIG_ACTIVITIES = "RigActivities",
  OBJECTIVE = "Objective",
  ASSIGNMENT = "Assignment",
  ACTIONABLE_ASSIGNMENTS = "ActionableAssignments",
  RECOMMENDED_OBJECTIVES = "RecommendedObjectives",
  INTERVENTION = "Intervention",
  INTERVENTION_LIST = "InterventionList",
  TASK = "Task",
  CHECKLIST_DEFINITION = "ChecklistDefinition",
  CHECKLIST_EXEMPTION = "ChecklistExemption",
  CHECKLIST = "Checklist",
  PATH_PLAN_REQUEST = "PathPlanRequest",
  WATCH_POOLS_LIST = "WatchPoolsList",
  WATCH_POOL_ROBOT_ASSIGNMENT = "WatchPoolRobotAssignment",
  WATCH_POOL_WATCHER_ASSIGNMENT = "WatchPoolWatcherAssignment",
  WATCH_POOL_ROBOTS_LIST = "WatchPoolRobotsList",
  WATCH_POOL_WATCHERS_LIST = "WatchPoolWatchersList",
  WATCH_POOL_ACTIVE_ROBOTS = "WatchPoolActiveRobots",
  WATCH_POOL_ACTIVE_WATCHERS = "WatchPoolActiveWatchers",
}

export type ListJobsQuery = {
  pageSize?: number;
  pageToken?: string;
  fieldIds?: string[];
  rigIds?: string[];
  ids?: number[];
  dryRuns?: boolean;
  creationFailures?: boolean;
  namePrefix?: string;
  nameSuffix?: string;
  farmIds?: string[];
  customerIds?: string[];
  states?: State[];
};

export const rtcJobsApi = createApi({
  reducerPath: "rtcJobsApi",
  baseQuery,
  tagTypes: Object.values(JobsAPITag),
  endpoints: (builder) => ({
    listInfiniteJobs: builder.infiniteQuery<
      ListJobsResponse,
      ListJobsQuery,
      string | undefined
    >({
      query: ({ queryArg, pageParam }) => ({
        url: "/jobs",
        params: {
          ids: queryArg.ids,
          page_size: queryArg.pageSize,
          page_token: pageParam,
          field_ids: queryArg.fieldIds,
          dry_runs: queryArg.dryRuns,
          creation_failures: queryArg.creationFailures,
          name_prefix: queryArg.namePrefix,
          name_suffix: queryArg.nameSuffix,
          farm_ids: queryArg.farmIds,
          customer_ids: queryArg.customerIds,
          states: queryArg.states,
          rig_ids: queryArg.rigIds,
        },
      }),
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.pageToken,
      },
      transformResponse: (response: unknown) =>
        ListJobsResponse.fromJSON(response),
      providesTags: (resp) => [
        JobsAPITag.JOB,
        ...(resp?.pages.flatMap((p) =>
          p.jobs.map((j) => ({
            type: JobsAPITag.JOB,
            id: j.id,
          }))
        ) ?? []),
      ],
    }),
    listJobs: builder.query<
      ListJobsResponse,
      {
        pageSize?: number;
        pageToken?: string;
        fieldIds?: string[];
        ids?: number[];
      }
    >({
      query: ({ pageSize, pageToken, fieldIds, ids }) => ({
        url: "/jobs",
        params: {
          ids,
          page_size: pageSize,
          page_token: pageToken,
          field_ids: fieldIds,
        },
      }),
      providesTags: (resp) => [
        JobsAPITag.JOB,
        ...(resp?.jobs.map((j) => ({
          type: JobsAPITag.JOB,
          id: j.id,
        })) ?? []),
      ],
      transformResponse: (response: unknown) =>
        ListJobsResponse.fromJSON(response),
    }),

    listStartupChecklistDefinitions: builder.query<
      ListChecklistDefinitionsResponse,
      void
    >({
      query: () => ({
        url: "/checklist-definitions",
      }),
      transformResponse: (response: unknown) =>
        ListChecklistDefinitionsResponse.fromJSON(response),
      providesTags: [{ type: JobsAPITag.CHECKLIST_DEFINITION, id: "LIST" }],
    }),
    getChecklistDefinition: builder.query<ChecklistDefinition, { id: string }>({
      query: ({ id }) => ({
        url: `/checklist-definitions/${id}`,
      }),
      transformResponse: (response: unknown) =>
        ChecklistDefinition.fromJSON(response),
      providesTags: (_resp, _err, { id }) => [
        { type: JobsAPITag.CHECKLIST_DEFINITION, id },
      ],
    }),
    createChecklistDefinition: builder.mutation<
      ChecklistDefinition,
      { name: string; description: string }
    >({
      query: ({ name, description }) => ({
        url: "/checklist-definitions",
        method: "POST",
        body: {
          content: {
            name,
            description,
          },
        },
      }),
      transformResponse: (response: unknown) =>
        ChecklistDefinition.fromJSON(response),
      invalidatesTags: [{ type: JobsAPITag.CHECKLIST_DEFINITION, id: "LIST" }],
    }),

    updateChecklistDefinition: builder.mutation<
      ChecklistDefinition,
      { id: string; name?: string; description?: string }
    >({
      query: ({ id, name, description }) => {
        const content: Record<string, string> = {};
        if (name !== undefined) {
          content.name = name;
        }
        if (description !== undefined) {
          content.description = description;
        }
        return {
          url: `/checklist-definitions/${id}`,
          method: "PATCH",
          body: { content },
        };
      },
      transformResponse: (response: unknown) =>
        ChecklistDefinition.fromJSON(response),
      invalidatesTags: (_resp, _err, { id }) => [
        { type: JobsAPITag.CHECKLIST_DEFINITION, id },
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: "LIST" },
      ],
    }),

    deleteChecklistDefinition: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/checklist-definitions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_resp, _err, { id }) => [
        { type: JobsAPITag.CHECKLIST_DEFINITION, id },
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: "LIST" },
      ],
    }),

    createChecklistItemDefinition: builder.mutation<
      ChecklistItemDefinition,
      {
        checklistDefinitionId: string;
        label: string;
        translationKey?: string;
        automationCode?: ChecklistAutomationCode;
        position: number;
      }
    >({
      query: ({
        checklistDefinitionId,
        label,
        translationKey,
        automationCode,
        position,
      }) => {
        const content: Record<string, unknown> = { label, position };

        if (translationKey && translationKey.trim().length > 0) {
          content.translationKey = translationKey;
        }

        if (
          automationCode !== undefined &&
          automationCode !== ChecklistAutomationCode.UNRECOGNIZED
        ) {
          content.automationCode =
            checklistAutomationCodeToJSON(automationCode);
        }

        return {
          url: `/checklist-definitions/${checklistDefinitionId}/item-definitions`,
          method: "POST",
          body: { content },
        };
      },
      transformResponse: (response: unknown) =>
        ChecklistItemDefinition.fromJSON(response),
      invalidatesTags: (_resp, _err, { checklistDefinitionId }) => [
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: checklistDefinitionId },
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: "LIST" },
      ],
    }),

    updateChecklistItemDefinition: builder.mutation<
      ChecklistItemDefinition,
      {
        checklistDefinitionId: string;
        id: string;
        label: string;
        translationKey?: string;
        automationCode?: ChecklistAutomationCode;
      }
    >({
      query: ({
        checklistDefinitionId,
        id,
        label,
        translationKey,
        automationCode,
      }) => {
        const content: Record<string, unknown> = { label };

        if (translationKey && translationKey.trim().length > 0) {
          content.translationKey = translationKey;
        }

        if (
          automationCode !== undefined &&
          automationCode !== ChecklistAutomationCode.UNRECOGNIZED
        ) {
          content.automationCode =
            checklistAutomationCodeToJSON(automationCode);
        }

        return {
          url: `/checklist-definitions/${checklistDefinitionId}/item-definitions/${id}`,
          method: "PATCH",
          body: { content },
        };
      },
      transformResponse: (response: unknown) =>
        ChecklistItemDefinition.fromJSON(response),
      invalidatesTags: (_resp, _err, { checklistDefinitionId }) => [
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: checklistDefinitionId },
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: "LIST" },
      ],
    }),

    deleteChecklistItemDefinition: builder.mutation<
      void,
      { checklistDefinitionId: string; id: string }
    >({
      query: ({ checklistDefinitionId, id }) => ({
        url: `/checklist-definitions/${checklistDefinitionId}/item-definitions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_resp, _err, { checklistDefinitionId }) => [
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: checklistDefinitionId },
        { type: JobsAPITag.CHECKLIST_DEFINITION, id: "LIST" },
      ],
    }),
    createJob: builder.mutation<Job, { job: CreateJobRequest }>({
      query: ({ job }) => ({
        url: "/jobs",
        method: "POST",
        body: job,
      }),
      invalidatesTags: (result) => [
        JobsAPITag.JOB,
        ...(result?.id ? [{ type: JobsAPITag.JOB, id: result.id }] : []),
      ],
    }),
    createBulkJobs: builder.mutation<JobList, CreateJobsRequest>({
      query: (req) => ({
        url: "/jobs/bulk",
        method: "POST",
        body: req,
      }),
      invalidatesTags: (result) => [
        JobsAPITag.JOB,
        ...(result?.jobs ?? []).map((job) => ({
          type: JobsAPITag.JOB,
          id: job.id,
        })),
      ],
    }),
    cloneJob: builder.mutation<
      CloneJobResponse,
      { id: string; request: CloneJobRequest }
    >({
      query: ({ id, request }) => ({
        url: `/jobs/${id}/clone`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (result) => [
        JobsAPITag.JOB,
        ...(result?.job?.id
          ? [{ type: JobsAPITag.JOB, id: result.job.id }]
          : []),
      ],
    }),
    getJob: builder.query<Job, { id: string }>({
      query: ({ id }) => ({
        url: `/jobs/${id}`,
      }),
      transformResponse: (response: unknown) => Job.fromJSON(response),
      providesTags: (resp, err, { id }) =>
        resp ? [{ type: JobsAPITag.JOB, id }] : [JobsAPITag.JOB],
    }),
    getConfiguredJob: builder.query<ConfiguredJob, { id: string }>({
      queryFn: async ({ id }, _queryApi, _extraOptions, fetchWithBQ) => {
        const jobResult = await fetchWithBQ({
          url: `/jobs/${id}`,
          method: "GET",
        });

        if (jobResult.error) {
          return { error: jobResult.error };
        }

        const job: AnyJob = Job.fromJSON(jobResult.data);

        const jobConfigResult = await fetchWithBQ({
          url: `/jobs/${id}/config`,
          method: "GET",
        });

        if (jobConfigResult.error) {
          return { error: jobConfigResult.error };
        }

        const jobConfig = JobConfiguration.fromJSON(jobConfigResult.data);

        const configuredJob = configureJob(job, jobConfig);
        return configuredJob
          ? { data: configuredJob }
          : {
              error: {
                status: "CUSTOM_ERROR",
                error: `no ${jobTypeToJSON(
                  job.type
                )} configuration for job ${id}`,
              },
            };
      },
      providesTags: (resp, err, { id }) => {
        return [
          { type: JobsAPITag.JOB, id },
          { type: JobsAPITag.JOB_CONFIG, id },
        ];
      },
    }),
    getObjective: builder.query<Objective, { id: number }>({
      query: ({ id }) => ({
        url: `/objectives/${id}`,
      }),
      transformResponse: (response: unknown) => Objective.fromJSON(response),
      providesTags: (resp, err, { id }) =>
        resp ? [{ type: JobsAPITag.OBJECTIVE, id }] : [],
    }),
    listObjectives: builder.query<
      ObjectiveList,
      {
        name_prefix?: string;
        name_suffix?: string;
        ids?: number[]; // list of objectiveIds
        jobIds?: number[]; // list of job ids
        types?: string[]; // list of job types
        priorities?: string[]; // list of priorities
        page_size?: number;
        page_token?: string;
      }
    >({
      query: ({ ids, jobIds, types, priorities, ...rest }) => ({
        url: `/objectives`,
        params: {
          ids,
          job_ids: jobIds,
          types,
          priorities,
          ...rest,
        },
      }),
      transformResponse: (response: unknown) =>
        ObjectiveList.fromJSON(response),
      providesTags: (resp) => [
        JobsAPITag.OBJECTIVE,
        ...(resp?.objectives.map((ob) => ({
          type: JobsAPITag.OBJECTIVE,
          id: ob.id,
        })) ?? []),
      ],
    }),
    listAssignments: builder.query<
      ListObjectiveAssignmentsResponse,
      {
        objectiveIds?: string[];
        robotSerials?: string[];
        pageSize?: number;
        pageToken?: string;
      }
    >({
      query: ({ objectiveIds, pageSize, pageToken, robotSerials }) => ({
        url: `/objective-assignments`,
        params: {
          objective_ids: objectiveIds,
          robot_serials: robotSerials,
          page_size: pageSize,
          page_token: pageToken,
        },
      }),
      transformResponse: (resp: unknown) =>
        ListObjectiveAssignmentsResponse.fromJSON(resp),
      providesTags: (resp) => [
        JobsAPITag.ASSIGNMENT,
        ...(resp?.assignments.map((ass) => ({
          type: JobsAPITag.ASSIGNMENT,
          id: ass.id,
        })) ?? []),
      ],
    }),
    listActionableAssignments: builder.query<
      ListObjectiveAssignmentsResponse,
      {
        robotSerial: string;
      }
    >({
      query: ({ robotSerial }) => ({
        url: `/robots/${robotSerial}/actionable-objective-assignments`,
      }),
      transformResponse: (resp: unknown) =>
        ListObjectiveAssignmentsResponse.fromJSON(resp),
      providesTags: (_result, _error, { robotSerial }) => [
        { type: JobsAPITag.ACTIONABLE_ASSIGNMENTS, serial: robotSerial },
      ],
    }),
    listRecommendedObjectives: builder.query<
      ObjectiveList,
      { robotSerial: string; jobId: number }
    >({
      query: ({ robotSerial, jobId }) => ({
        url: `/robots/${robotSerial}/recommended-objectives`,
        params: {
          job_id: jobId,
        },
      }),
      transformResponse: (response: unknown) =>
        ObjectiveList.fromJSON(response),
      providesTags: (resp, _err, { robotSerial }) => [
        { type: JobsAPITag.RECOMMENDED_OBJECTIVES, id: robotSerial },
        ...(resp?.objectives.map((ob) => ({
          type: JobsAPITag.OBJECTIVE,
          id: ob.id,
        })) ?? []),
      ],
    }),
    setAssignment: builder.mutation<
      ObjectiveAssignment,
      { objectiveId: number; robotSerial: string }
    >({
      query: ({ objectiveId, robotSerial }) => ({
        url: `/objectives/${objectiveId}/assignment`,
        method: Method.PUT,
        body: { robotSerial },
      }),
      transformResponse: (response: unknown) =>
        ObjectiveAssignment.fromJSON(response),
      onQueryStarted: async (
        { objectiveId, robotSerial },
        { dispatch, queryFulfilled }
      ) => {
        const { data: assignment } = await queryFulfilled;
        dispatch(
          rtcJobsApi.util.invalidateTags([
            JobsAPITag.ASSIGNMENT,
            { type: JobsAPITag.ASSIGNMENT, id: assignment.id },
          ])
        );
        dispatch(
          rtcJobsApi.util.invalidateTags([
            { type: JobsAPITag.OBJECTIVE, id: objectiveId },
          ])
        );
        dispatch(
          rtcJobsApi.util.invalidateTags([
            { type: JobsAPITag.RECOMMENDED_OBJECTIVES, id: robotSerial },
          ])
        );
      },
    }),
    updateAssignmentPriorities: builder.mutation<
      ListObjectiveAssignmentsResponse,
      ObjectiveAssignmentList
    >({
      query: (req) => ({
        url: `/objective-assignments`,
        method: Method.PATCH,
        body: req,
      }),
      transformResponse: (response: unknown) =>
        ListObjectiveAssignmentsResponse.fromJSON(response),
      invalidatesTags: (resp) => [
        ...(resp?.assignments ?? []).map((asgn) => ({
          type: JobsAPITag.ASSIGNMENT,
          id: asgn.id,
        })),
        // reordering changes where the robot ends up after its queue, which
        // feeds the recommendation algorithm
        ...[
          ...new Set((resp?.assignments ?? []).map((asgn) => asgn.robotSerial)),
        ].map((serial) => ({
          type: JobsAPITag.RECOMMENDED_OBJECTIVES,
          id: serial,
        })),
      ],
    }),
    removeAssignment: builder.mutation<
      ObjectiveAssignment,
      { objectiveId: number }
    >({
      query: ({ objectiveId }) => ({
        url: `/objectives/${objectiveId}/assignment`,
        method: Method.DELETE,
      }),
      transformResponse: (response: unknown) =>
        ObjectiveAssignment.fromJSON(response),
      onQueryStarted: async ({ objectiveId }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          rtcJobsApi.util.updateQueryData("listAssignments", {}, (resp) => {
            spliceIfExists(
              resp.assignments,
              (assignment) => assignment.id === data.id,
              1
            );
            return resp;
          })
        );
        dispatch(
          rtcJobsApi.util.invalidateTags([
            JobsAPITag.ASSIGNMENT,
            { type: JobsAPITag.ASSIGNMENT, id: data.id },
          ])
        );
        dispatch(
          rtcJobsApi.util.invalidateTags([
            { type: JobsAPITag.OBJECTIVE, id: objectiveId },
          ])
        );
        dispatch(
          rtcJobsApi.util.invalidateTags([JobsAPITag.RECOMMENDED_OBJECTIVES])
        );
      },
    }),
    updateJob: builder.mutation<
      Job,
      { job: Partial<Job> & { id: number }; generateObjectives: boolean }
    >({
      query: ({ job, ...params }) => ({
        url: `/jobs/${job.id}`,
        params: transformKeys(params, camelToSnake),
        method: "PATCH",
        body: job,
      }),
      transformResponse: (response: unknown) => Job.fromJSON(response),
      invalidatesTags: (_resp, _error, { job }) => [
        { type: JobsAPITag.JOB, id: job.id },
      ],
    }),
    updateAvailableRobots: builder.mutation<
      { entries: { robotSerial: string }[] },
      { jobId: number; serials: string[] }
    >({
      query: ({ jobId, serials }) => ({
        url: `/jobs/${jobId}/robot-whitelist`,
        method: "PUT",
        body: {
          entries: serials.map((serial) => ({ robotSerial: serial })),
        },
      }),
      invalidatesTags: (_resp, _error, { jobId }) => [
        { type: JobsAPITag.JOB, id: jobId },
      ],
    }),
    updateObjective: builder.mutation<
      Objective,
      Partial<Objective> & Pick<Objective, "id">
    >({
      query: (params) => ({
        url: `/objectives/${params.id}`,
        method: "PATCH",
        body: transformKeys(params, camelToSnake),
      }),
      transformResponse: (response: unknown) => Objective.fromJSON(response),
      invalidatesTags: (_resp, _err, { id }) => [
        { type: JobsAPITag.OBJECTIVE, id },
      ],
    }),
    updateObjectivePriorities: builder.mutation<
      void,
      { jobId: number; changes: { id: number; priority: number }[] }
    >({
      queryFn: async (params, _queryApi, _extraOptions, fetchWithBQ) => {
        const updateResult = await fetchWithBQ({
          url: "/objectives",
          method: "PATCH",
          body: {
            objectives: params.changes,
          },
        });

        if (updateResult.error) {
          return { error: updateResult.error as FetchBaseQueryError };
        }

        return { data: undefined };
      },
      invalidatesTags: (_resp, _error, { jobId }) => [
        { type: JobsAPITag.JOB, id: jobId },
      ],
    }),
    deleteJob: builder.mutation<Job, { id: number }>({
      query: ({ id }) => ({
        url: `/jobs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_resp, _error, { id }) => [
        JobsAPITag.JOB,
        { type: JobsAPITag.JOB, id },
      ],
    }),
    changeJobState: builder.mutation<Job, { id: number; state: State }>({
      query: ({ id, state }) => ({
        url: `/jobs/${id}/state`,
        method: "PUT",
        body: { state: stateToJSON(state) },
      }),
      invalidatesTags: (_resp, _error, { id }) => [
        { type: JobsAPITag.JOB, id },
      ],
    }),
    updateJobConfig: builder.mutation<
      JobConfiguration,
      {
        id: number;
        generateObjectives: boolean;
        config: JobConfiguration;
      }
    >({
      query: ({ id, config, ...params }) => ({
        url: `/jobs/${id}/config`,
        method: "PUT",
        body: JobConfiguration.toJSON(config),
        params: transformKeys(params, camelToSnake),
      }),
      invalidatesTags: (_resp, _error, { id }) => {
        return [
          { type: JobsAPITag.JOB, id },
          { type: JobsAPITag.JOB_CONFIG, id },
        ];
      },
    }),
    /** @deprecated post-M6, this should be configured via the rig activities API */
    setGroundPrepToolSpeed: builder.mutation<
      ToolSpeed,
      Omit<ToolSpeed & { id: number }, "toolSpeedKmh">
    >({
      query: (params) => ({
        url: `/jobs/${params.id}/config/tool-speed`,
        method: "PUT",
        body: transformKeys({ toolSpeedMS: params.toolSpeedMS }, camelToSnake),
      }),
      invalidatesTags: (_resp, _err, { id }) =>
        _resp
          ? [
              { type: JobsAPITag.JOB_CONFIG, id },
              { type: JobsAPITag.JOB, id },
            ]
          : [],
    }),
    /** @deprecated post-M6, this should be configured via the rig activities API */
    setTransitionSpeed: builder.mutation<
      TransitionSpeed,
      Omit<TransitionSpeed & { id: number }, "transitionSpeedKmh">
    >({
      query: (params) => ({
        url: `/jobs/${params.id}/config/transition-speed`,
        method: "PUT",
        body: transformKeys(
          { transitionSpeedMS: params.transitionSpeedMS },
          camelToSnake
        ),
      }),
      invalidatesTags: (_resp, _err, { id }) =>
        _resp
          ? [
              { type: JobsAPITag.JOB_CONFIG, id },
              { type: JobsAPITag.JOB, id },
            ]
          : [],
    }),
    setRowTraversalSpeed: builder.mutation<
      RowTraversalSpeed,
      RowTraversalSpeed & { id: number }
    >({
      query: (params) => ({
        url: `/jobs/${params.id}/config/row-traversal-speed`,
        method: "PUT",
        body: transformKeys(
          { rowTraversalSpeedMS: params.rowTraversalSpeedMS },
          camelToSnake
        ),
      }),
      invalidatesTags: (_resp, _err, { id }) =>
        _resp
          ? [
              { type: JobsAPITag.JOB_CONFIG, id },
              { type: JobsAPITag.JOB, id },
            ]
          : [],
    }),
    setTransportSpeed: builder.mutation<
      TransportSpeed,
      TransportSpeed & { id: number }
    >({
      query: (params) => ({
        url: `/jobs/${params.id}/config/transport-speed`,
        method: "PUT",
        body: transformKeys(
          { transportSpeedMS: params.transportSpeedMS },
          camelToSnake
        ),
      }),
      invalidatesTags: (_resp, _err, { id }) =>
        _resp
          ? [
              { type: JobsAPITag.JOB_CONFIG, id },
              { type: JobsAPITag.JOB, id },
            ]
          : [],
    }),
    autoCompleteRows: builder.mutation<
      void,
      { jobId: number; robotSerials: string[]; lookbackWindow: Duration }
    >({
      query: ({ jobId, lookbackWindow, ...params }) => ({
        url: `/jobs/${jobId}/objectives/auto-complete`,
        method: "POST",
        body: transformKeys(
          {
            ...params,
            lookbackWindow: Duration.toJSON(lookbackWindow),
          },
          camelToSnake
        ),
      }),
      invalidatesTags: () => [JobsAPITag.OBJECTIVE],
    }),
    getActiveObjective: builder.query<
      Objective | undefined,
      { serial: string }
    >({
      queryFn: async ({ serial }, _api, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ(`/robots/${serial}/active-objective`);
        if (result.error) {
          if (result.error.status === 404) {
            return { data: undefined };
          }
          return { error: result.error };
        }
        return { data: Objective.fromJSON(result.data) };
      },
      providesTags: (resp, _err) =>
        resp ? [{ type: JobsAPITag.OBJECTIVE, id: resp.id }] : [],
    }),
    deleteActiveObjective: builder.mutation<
      Partial<Objective>,
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/robots/${serial}/active-objective`,
        method: "DELETE",
      }),
      transformResponse: (response: unknown) => Objective.fromJSON(response),
      invalidatesTags: (resp, _err) =>
        resp
          ? [
              { type: JobsAPITag.OBJECTIVE, id: resp.id },
              { type: JobsAPITag.ASSIGNMENT, id: resp.assignment?.id },
            ]
          : [],
    }),
    getActiveAtomicTask: builder.query<Task | undefined, { serial: string }>({
      queryFn: async ({ serial }, api, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: `/robots/${serial}/active-atomic-task`,
          params: { include_next_task: true },
        });
        if (result.error) {
          if (result.error.status === 404) {
            return { data: undefined };
          }
          return { error: result.error };
        }
        return { data: Task.fromJSON(result.data) };
      },
      providesTags: (resp, _err) =>
        resp ? [{ type: JobsAPITag.TASK, id: resp.id }] : [],
    }),
    getActiveTask: builder.query<Task | undefined, { serial: string }>({
      query: ({ serial }) => ({
        url: `/robots/${serial}/active-task`,
        params: {
          include_next_task: true,
        },
      }),
      transformResponse: (response: unknown) => Task.fromJSON(response),
      providesTags: (resp, _err) =>
        resp ? [{ type: JobsAPITag.TASK, id: resp.id }] : [],
    }),
    getTask: builder.query<Task, { id: number }>({
      query: ({ id }) => ({
        url: `/tasks/${id}`,
      }),
      transformResponse: (response: unknown) => Task.fromJSON(response),
      providesTags: (resp, _err, { id }) =>
        resp ? [{ type: JobsAPITag.TASK, id }] : [],
    }),
    startManualTask: builder.mutation<
      Task,
      StartManualTaskRequest & { id: number }
    >({
      query: ({ id, ...params }) => ({
        url: `/tasks/${id}/manual-start`,
        method: "POST",
        body: transformKeys(params, camelToSnake),
      }),
      invalidatesTags: (_resp, _err, { id }) => [{ type: JobsAPITag.TASK, id }],
    }),
    stopManualTask: builder.mutation<
      Task,
      StopManualTaskRequest & { id: number }
    >({
      query: ({ id, ...params }) => ({
        url: `/tasks/${id}/manual-stop`,
        body: transformKeys(params, camelToSnake),
        method: "POST",
      }),
      invalidatesTags: (_resp, _err, { id }) => [{ type: JobsAPITag.TASK, id }],
    }),
    updateManualTask: builder.mutation<
      Task,
      Pick<UpdateTaskRequest, "progressLocation"> &
        Pick<UpdateTaskRequest, "progressHeading"> & { id: number }
    >({
      query: ({ id, ...params }) => ({
        url: `/tasks/${id}/manual-update`,
        body: transformKeys(params, camelToSnake),
        method: "POST",
      }),
      invalidatesTags: (_resp, _err, { id }) => [{ type: JobsAPITag.TASK, id }],
    }),
    createIntervention: builder.mutation<
      Intervention,
      Partial<Omit<Intervention, "id">>
    >({
      query: (params) => ({
        url: `/interventions`,
        method: "POST",
        body: transformKeys(params, camelToSnake),
      }),
      transformResponse: (response: unknown) => Intervention.fromJSON(response),
      invalidatesTags: (resp) =>
        resp
          ? [
              JobsAPITag.INTERVENTION_LIST,
              { type: JobsAPITag.INTERVENTION, id: resp.id },
            ]
          : [JobsAPITag.INTERVENTION_LIST],
    }),
    updateIntervention: builder.mutation<
      Intervention,
      Partial<Intervention> & { id: number }
    >({
      query: (params) => ({
        url: `/interventions/${params.id}`,
        method: "PATCH",
        body: transformKeys(params, camelToSnake),
      }),
      transformResponse: (response: unknown) => Intervention.fromJSON(response),
      invalidatesTags: (_resp, _err, { id }) => [
        { type: JobsAPITag.INTERVENTION, id },
      ],
    }),
    assignIntervention: builder.mutation<
      InterventionAssignment,
      { id: number; userId: string }
    >({
      query: ({ id, userId }) => ({
        url: `/interventions/${id}/assignment`,
        method: Method.PUT,
        body: transformKeys({ userId }, camelToSnake),
      }),
      transformResponse: (response: unknown) =>
        InterventionAssignment.fromJSON(response),
      invalidatesTags: (_resp, _err, { id }) => [
        { type: JobsAPITag.INTERVENTION, id },
      ],
    }),
    listObjectiveInterventions: builder.query<
      ListInterventionsResponse,
      { objectiveId: string }
    >({
      query: (params) => ({
        url: `/objectives/${params.objectiveId}/interventions`,
      }),
      transformResponse: (response: unknown) =>
        ListInterventionsResponse.fromJSON(response),
      providesTags: (resp) =>
        resp
          ? [
              JobsAPITag.INTERVENTION,
              ...resp.interventions.map((intv) => ({
                type: JobsAPITag.INTERVENTION,
                id: intv.id,
              })),
            ]
          : [JobsAPITag.INTERVENTION],
    }),
    listInterventions: builder.query<
      ListInterventionsResponse,
      Partial<ListInterventionsRequest>
    >({
      query: (params) => ({
        url: "/interventions",
        params: transformKeys(params, camelToSnake),
      }),
      transformResponse: (response: unknown) =>
        ListInterventionsResponse.fromJSON(response),
      providesTags: (resp) =>
        resp
          ? [
              JobsAPITag.INTERVENTION,
              ...resp.interventions.map((intv) => ({
                type: JobsAPITag.INTERVENTION,
                id: intv.id,
              })),
            ]
          : [JobsAPITag.INTERVENTION],
    }),
    // this endpoint is not guaranteed to return tasks in any specific order
    listTasks: builder.query<
      ListTasksResponse,
      {
        objectiveIds?: number[];
        states?: State[];
        pageSize?: number;
        pageToken?: string;
      }
    >({
      query: (params) => ({
        url: `/tasks`,
        params: transformKeys(params, camelToSnake),
      }),
      transformResponse: (response: unknown) =>
        ListTasksResponse.fromJSON(response),
      providesTags: (resp) =>
        resp
          ? [
              JobsAPITag.TASK,
              ...resp.tasks.map((t) => ({
                type: JobsAPITag.TASK,
                id: t.id,
              })),
            ]
          : [],
    }),
    // this endpoint will return tasks in the order they should be done by a robot
    listObjectiveTasks: builder.query<
      ListTasksResponse,
      { objectiveId: number }
    >({
      query: ({ objectiveId }) => ({
        url: `/objectives/${objectiveId}/tasks`,
      }),
      transformResponse: (response: unknown) =>
        ListTasksResponse.fromJSON(response),
      providesTags: (resp) =>
        resp
          ? [
              JobsAPITag.TASK,
              ...resp.tasks.map((t) => ({
                type: JobsAPITag.TASK,
                id: t.id,
              })),
            ]
          : [],
    }),
    updateTask: builder.mutation<
      UpdateTaskResponse,
      Partial<Task> & Pick<Task, "id">
    >({
      query: (params) => ({
        url: `/tasks/${params.id}`,
        method: "PATCH",
        body: transformKeys(params, camelToSnake),
      }),
      transformResponse: (response: unknown) =>
        UpdateTaskResponse.fromJSON(response),
      invalidatesTags: (_resp, _err, { id }) => [{ type: JobsAPITag.TASK, id }],
    }),
    setTaskState: builder.mutation<
      UpdateTaskResponse,
      Pick<Task, "id" | "state"> & { override?: boolean }
    >({
      query: ({ id, state, ...params }) => ({
        url: `/tasks/${id}/state`,
        method: "PUT",
        body: { state: stateToJSON(state) },
        params: transformKeys(params, camelToSnake),
      }),
      transformResponse: (response: unknown) =>
        UpdateTaskResponse.fromJSON(response),
      invalidatesTags: (_resp, _err, { id }) => [{ type: JobsAPITag.TASK, id }],
    }),
    getPivotRiskObjectives: builder.query<
      PivotRiskObjectivesResponse,
      { jobId: string; forecast?: string }
    >({
      query: ({ jobId, forecast }) => ({
        url: `/jobs/${jobId}/pivot-risk-objectives`,
        params: { forecast },
      }),
      transformResponse: (response: unknown) =>
        PivotRiskObjectivesResponse.fromJSON(response),
      keepUnusedDataFor: 0,
    }),
    getChecklistExemption: builder.query<
      ChecklistExemption | undefined,
      { serial: string; fieldId?: string }
    >({
      query: ({ serial, fieldId }) => ({
        url: `/robots/${serial}/checklist-exemption`,
        method: "GET",
        params: { field_id: fieldId },
      }),
      transformResponse: (response: unknown) =>
        response === undefined
          ? undefined
          : ChecklistExemption.fromJSON(response),
      providesTags: (_1, _2, { serial }) => [
        { type: JobsAPITag.CHECKLIST_EXEMPTION, id: serial },
      ],
    }),
    setChecklistExemption: builder.mutation<
      ChecklistExemption,
      { serial: string; validDuration?: Duration; fieldId?: string }
    >({
      query: ({ serial, validDuration, fieldId }) => ({
        url: `/robots/${serial}/checklist-exemption`,
        method: "POST",
        body: {
          valid_duration: validDuration
            ? Duration.toJSON(validDuration)
            : undefined,
          field_id: fieldId,
        },
      }),
      transformResponse: (response: unknown) =>
        ChecklistExemption.fromJSON(response),
      invalidatesTags: (_1, _2, { serial }) => [
        { type: JobsAPITag.CHECKLIST_EXEMPTION, id: serial },
      ],
    }),
    getLatestValidChecklist: builder.query<
      Checklist,
      { robotSerial: string; fieldId: string }
    >({
      query: ({ ...params }) => ({
        url: "/checklists/latest-valid",
        params: transformKeys(params, camelToSnake),
        transformResponse: (response: unknown) => Checklist.fromJSON(response),
        providesTags: [
          {
            type: JobsAPITag.CHECKLIST,
            id: `${params.robotSerial}::${params.fieldId}`,
          },
        ],
      }),
    }),
    /** @deprecated post M6, use rig activities configuration API */
    getScvActivitiesConfiguration: builder.query<
      ScvAutonomyActivitiesConfiguration,
      { jobId: string }
    >({
      query: ({ jobId }) => ({
        url: `/jobs/${jobId}/config/scv-activities`,
      }),
      transformResponse: (response: unknown) =>
        ScvAutonomyActivitiesConfiguration.fromJSON(response),
      providesTags: (_resp, err, { jobId }) =>
        err ? [] : [{ type: JobsAPITag.SCV_ACTIVITIES, id: jobId }],
    }),
    /** @deprecated post M6, use rig activities configuration API */
    setScvActivitiesConfiguration: builder.mutation<
      ScvAutonomyActivitiesConfiguration,
      { jobId: string; config: ScvAutonomyActivitiesConfiguration }
    >({
      query: ({ jobId, config }) => ({
        url: `/jobs/${jobId}/config/scv-activities`,
        method: "PUT",
        body: config,
      }),
      transformResponse: (response: unknown) =>
        ScvAutonomyActivitiesConfiguration.fromJSON(response),
      invalidatesTags: (_resp, _err, { jobId }) => [
        { type: JobsAPITag.SCV_ACTIVITIES, id: jobId },
      ],
    }),
    getRigActivitiesConfiguration: builder.query<
      RigActivitiesConfiguration,
      { jobId: string }
    >({
      query: ({ jobId }) => ({
        url: `/jobs/${jobId}/config/rig-activities`,
      }),
      transformResponse: (response: unknown) =>
        RigActivitiesConfiguration.fromJSON(response),
      providesTags: (_resp, err, { jobId }) =>
        err ? [] : [{ type: JobsAPITag.RIG_ACTIVITIES, id: jobId }],
    }),
    setRigActivitiesConfiguration: builder.mutation<
      RigActivitiesConfiguration,
      { jobId: string; config: RigActivitiesConfiguration }
    >({
      query: ({ jobId, config }) => ({
        url: `/jobs/${jobId}/config/rig-activities`,
        method: "PUT",
        body: RigActivitiesConfiguration.toJSON(config),
      }),
      transformResponse: (response: unknown) =>
        RigActivitiesConfiguration.fromJSON(response),
      invalidatesTags: (_resp, _err, { jobId }) => [
        { type: JobsAPITag.RIG_ACTIVITIES, id: jobId },
      ],
    }),
    alignAllRows: builder.mutation<
      Job,
      CalibrateLaserWeedJobRequest & { jobId: number }
    >({
      query: (req) => ({
        method: "POST",
        url: `/jobs/${req.jobId}/calibrate`,
        body: transformKeys(req, camelToSnake),
      }),
      invalidatesTags: (_resp, _err, req) => [
        { type: JobsAPITag.JOB, id: req.jobId },
      ],
    }),
    createEnterRowManeuver: builder.mutation<
      Objective,
      CreateEnterRowTaskRequest & { remainderTaskId: number }
    >({
      query: (req) => ({
        method: "POST",
        url: `/tasks/${req.remainderTaskId}/enter-row`,
        body: transformKeys(req, camelToSnake),
      }),
    }),
    createExitRowManeuver: builder.mutation<
      Objective,
      CreateExitRowTaskRequest & { remainderTaskId: number }
    >({
      query: (req) => ({
        method: "POST",
        url: `/tasks/${req.remainderTaskId}/exit-row`,
        body: transformKeys(req, camelToSnake),
      }),
    }),
    createDrawPathManeuver: builder.mutation<
      Task,
      CreateFollowPathTaskRequest & { jobId: number }
    >({
      query: (req) => ({
        method: "POST",
        url: `/jobs/${req.jobId}/follow-path`,
        body: transformKeys(req, camelToSnake),
      }),
    }),
    calibrateRow: builder.mutation<
      Task,
      CalibrateLaserWeedObjectiveRequest & { objectiveId: number }
    >({
      query: (req) => ({
        method: "POST",
        url: `/objectives/${req.objectiveId}/calibrate`,
        body: transformKeys(req, camelToSnake),
      }),
      invalidatesTags: (_resp, _error, req) => [
        { type: JobsAPITag.OBJECTIVE, id: req.objectiveId },
      ],
    }),
    getObjectivePathPlan: builder.query<
      LambdaPathPlannerRequest,
      { objectiveId: number }
    >({
      query: ({ objectiveId }) => ({
        url: `/objectives/${objectiveId}/path-planner-request`,
        transformResponse: (response: unknown) =>
          LambdaPathPlannerRequest.fromJSON(response),
        providesTags: [
          {
            type: JobsAPITag.PATH_PLAN_REQUEST,
            id: `${objectiveId}`,
          },
        ],
      }),
    }),
    listWatchPools: builder.query<ListWatchPoolsResponse, void>({
      query: () => ({
        url: `/watch-pools`,
      }),
      transformResponse: (response: unknown) =>
        ListWatchPoolsResponse.fromJSON(response),
      providesTags: [{ type: JobsAPITag.WATCH_POOLS_LIST }],
    }),
    // fetches a single pool by ID; 404s if it doesn't exist
    getWatchPool: builder.query<GetPoolResponse, { poolId: number }>({
      query: ({ poolId }) => ({
        url: `/watch-pools/${poolId}`,
      }),
      transformResponse: (response: unknown) =>
        GetPoolResponse.fromJSON(response),
      providesTags: (_resp, _err, { poolId }) => [
        { type: JobsAPITag.WATCH_POOLS_LIST, id: poolId },
      ],
    }),
    createWatchPool: builder.mutation<CreatePoolResponse, { name: string }>({
      query: (req) => ({
        url: `/watch-pools`,
        method: Method.POST,
        body: transformKeys(req, camelToSnake),
      }),
      transformResponse: (response: unknown) =>
        CreatePoolResponse.fromJSON(response),
      invalidatesTags: [{ type: JobsAPITag.WATCH_POOLS_LIST }],
    }),
    deleteWatchPool: builder.mutation<void, { poolId: number }>({
      query: ({ poolId }) => ({
        url: `/watch-pools/${poolId}`,
        method: Method.DELETE,
      }),
      invalidatesTags: [
        { type: JobsAPITag.WATCH_POOLS_LIST },
        JobsAPITag.WATCH_POOL_ROBOT_ASSIGNMENT,
        JobsAPITag.WATCH_POOL_WATCHER_ASSIGNMENT,
        JobsAPITag.WATCH_POOL_ACTIVE_ROBOTS,
      ],
    }),
    // assigns a robot into a pool's rotation; it can be served once online
    assignWatchPoolRobot: builder.mutation<
      void,
      { poolId: number; robotSerial: string }
    >({
      query: ({ poolId, robotSerial }) => ({
        url: `/watch-pools/${poolId}/robots/${robotSerial}`,
        method: Method.PUT,
      }),
      invalidatesTags: (_resp, _err, { robotSerial }) => [
        { type: JobsAPITag.WATCH_POOL_ROBOT_ASSIGNMENT, id: robotSerial },
        { type: JobsAPITag.WATCH_POOL_ROBOT_ASSIGNMENT, id: "LIST" },
        JobsAPITag.WATCH_POOL_ROBOTS_LIST,
        JobsAPITag.WATCH_POOL_ACTIVE_ROBOTS,
      ],
    }),
    // removes a robot's long-running pool assignment
    unassignWatchPoolRobot: builder.mutation<void, { robotSerial: string }>({
      query: ({ robotSerial }) => ({
        url: `/watch-pool-robots/${robotSerial}`,
        method: Method.DELETE,
      }),
      invalidatesTags: (_resp, _err, { robotSerial }) => [
        { type: JobsAPITag.WATCH_POOL_ROBOT_ASSIGNMENT, id: robotSerial },
        { type: JobsAPITag.WATCH_POOL_ROBOT_ASSIGNMENT, id: "LIST" },
        JobsAPITag.WATCH_POOL_ROBOTS_LIST,
        JobsAPITag.WATCH_POOL_ACTIVE_ROBOTS,
      ],
    }),
    // lists the robots assigned to a pool, in assignment order. This is the
    // persisted membership, which includes robots that are currently offline
    listWatchPoolRobots: builder.query<
      ListPoolRobotsResponse,
      { poolId: number }
    >({
      query: ({ poolId }) => ({
        url: `/watch-pools/${poolId}/robots`,
      }),
      transformResponse: (response: unknown) =>
        ListPoolRobotsResponse.fromJSON(response),
      providesTags: (_resp, _err, { poolId }) => [
        { type: JobsAPITag.WATCH_POOL_ROBOTS_LIST, id: poolId },
      ],
    }),
    // lists robot pool assignments across all pools, answering "which pool is
    // each robot in" in one request. robotSerials filters to specific robots;
    // omit it to list every assignment. Unassigned robots are absent from the
    // response rather than an error
    listWatchPoolRobotAssignments: builder.query<
      ListRobotAssignmentsResponse,
      { robotSerials?: string[] }
    >({
      query: ({ robotSerials }) => ({
        url: `/watch-pool-robots`,
        params: {
          // toQuery comma-joins arrays, matching the backend's format
          serials: robotSerials?.length ? robotSerials : undefined,
        },
      }),
      transformResponse: (response: unknown) =>
        ListRobotAssignmentsResponse.fromJSON(response),
      providesTags: (_resp, _err, { robotSerials }) =>
        robotSerials?.length
          ? robotSerials.map((serial) => ({
              type: JobsAPITag.WATCH_POOL_ROBOT_ASSIGNMENT,
              id: serial,
            }))
          : [{ type: JobsAPITag.WATCH_POOL_ROBOT_ASSIGNMENT, id: "LIST" }],
    }),
    // lists the robots currently online in their pool's rotation, sorted by
    // serial. Presence in the response IS onlineness — assigned-but-offline
    // robots are excluded, so compute them by diffing against pool membership
    // (listWatchPoolRobots / listWatchPoolRobotAssignments)
    listWatchPoolActiveRobots: builder.query<ListActiveRobotsResponse, void>({
      query: () => ({
        url: `/watch-pool-robots/active`,
      }),
      transformResponse: (response: unknown) =>
        ListActiveRobotsResponse.fromJSON(response),
      providesTags: [{ type: JobsAPITag.WATCH_POOL_ACTIVE_ROBOTS }],
    }),
    // clocks a watcher into a pool so they start pulling robots from its rotation
    assignWatchPoolWatcher: builder.mutation<
      void,
      { poolId: number; watcherId: string }
    >({
      query: ({ poolId, watcherId }) => ({
        url: `/watch-pools/${poolId}/watchers/${watcherId}`,
        method: Method.PUT,
      }),
      invalidatesTags: (_resp, _err, { watcherId }) => [
        { type: JobsAPITag.WATCH_POOL_WATCHER_ASSIGNMENT, id: watcherId },
        { type: JobsAPITag.WATCH_POOL_WATCHER_ASSIGNMENT, id: "LIST" },
        JobsAPITag.WATCH_POOL_WATCHERS_LIST,
      ],
    }),
    // clocks a watcher out, ending their pool assignment
    removeWatchPoolWatcher: builder.mutation<void, { watcherId: string }>({
      query: ({ watcherId }) => ({
        url: `/watch-pool-watchers/${watcherId}`,
        method: Method.DELETE,
      }),
      invalidatesTags: (_resp, _err, { watcherId }) => [
        { type: JobsAPITag.WATCH_POOL_WATCHER_ASSIGNMENT, id: watcherId },
        { type: JobsAPITag.WATCH_POOL_WATCHER_ASSIGNMENT, id: "LIST" },
        JobsAPITag.WATCH_POOL_WATCHERS_LIST,
      ],
    }),
    // looks up which pool a watcher is currently clocked into
    getWatchPoolWatcherAssignment: builder.query<
      GetWatcherAssignmentResponse,
      { watcherId: string }
    >({
      query: ({ watcherId }) => ({
        url: `/watch-pool-watchers/${watcherId}`,
      }),
      transformResponse: (response: unknown) =>
        GetWatcherAssignmentResponse.fromJSON(response),
      providesTags: (_resp, _err, { watcherId }) => [
        { type: JobsAPITag.WATCH_POOL_WATCHER_ASSIGNMENT, id: watcherId },
      ],
    }),
    // lists the watchers clocked into a pool, in assignment order
    listWatchPoolWatchers: builder.query<
      ListPoolWatchersResponse,
      { poolId: number }
    >({
      query: ({ poolId }) => ({
        url: `/watch-pools/${poolId}/watchers`,
      }),
      transformResponse: (response: unknown) =>
        ListPoolWatchersResponse.fromJSON(response),
      providesTags: (_resp, _err, { poolId }) => [
        { type: JobsAPITag.WATCH_POOL_WATCHERS_LIST, id: poolId },
      ],
    }),
    // clocks a watcher in: they appear active before their first robot pull.
    // Clocking in again restarts the shift, dropping any held robot pair
    clockInWatcher: builder.mutation<
      ClockInWatcherResponse,
      { watcherId: string }
    >({
      query: ({ watcherId }) => ({
        url: `/watch-pool-watchers/${watcherId}/clock-in`,
        method: Method.POST,
      }),
      transformResponse: (response: unknown) =>
        ClockInWatcherResponse.fromJSON(response),
      invalidatesTags: [{ type: JobsAPITag.WATCH_POOL_ACTIVE_WATCHERS }],
    }),
    // clocks a watcher out immediately instead of waiting for the timeout.
    // Clocking out a watcher who isn't clocked in succeeds
    clockOutWatcher: builder.mutation<void, { watcherId: string }>({
      query: ({ watcherId }) => ({
        url: `/watch-pool-watchers/${watcherId}/clock-out`,
        method: Method.POST,
      }),
      invalidatesTags: [{ type: JobsAPITag.WATCH_POOL_ACTIVE_WATCHERS }],
    }),
    // lists clocked-in watchers, with the robot pair each holds and their
    // last pull time
    listWatchPoolActiveWatchers: builder.query<
      ListActiveWatchersResponse,
      void
    >({
      query: () => ({
        url: `/watch-pool-watchers/active`,
      }),
      transformResponse: (response: unknown) =>
        ListActiveWatchersResponse.fromJSON(response),
      providesTags: [{ type: JobsAPITag.WATCH_POOL_ACTIVE_WATCHERS }],
    }),
    // returns the next robot in line for this watcher to pick up
    getWatchPoolNextRobot: builder.query<
      GetNextRobotResponse,
      GetNextRobotRequest
    >({
      query: ({ watcherId }) => ({
        url: `/watch-pool-watchers/${watcherId}/next-robot`,
      }),
      transformResponse: (response: unknown) =>
        GetNextRobotResponse.fromJSON(response),
    }),
  }),
});
